import styles from './Shipments.module.css';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Typography, Link as LinkButton, IconButton, Link } from '@mui/material';
import { Autorenew } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { LightTooltip } from '../dentalenhancements/components/ToolTip';
import NoImage from '../../../images/no_image.png';
import { categoryImages, itemImages, kitImages } from '../../../helpers/image';
import constants from '../../../constants';
import { CustomerHoldFlags, PeriodTotals, UpcomingShipment } from './types';
import {
  getRefItems,
  getUpcomingShipmentData,
  selectShipmentsData,
  selectRefItems,
  clearRefItems,
  clearShipments,
  setShipmentCustomerNumber,
} from './slice';
import { getDisplayPeriodName } from '../../../helpers/date';
import useCustomerNumber from '../../../hooks/useCustomerNumber';
import useIsCorpView from '../../../hooks/useIsCorpView';
import { getPeriodIds, getSubscriptions } from './utils/helpers';
import { LoadingState } from '../../../types'
import CreditHoldBanner from '../../../components/CreditHoldBanner'
import useCustomer from '../../../hooks/useCustomer';

const UpcomingShipments = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isCorpView = useIsCorpView()
  const customer = useCustomer()
  const customerNumber = useCustomerNumber()

  const refItems = useAppSelector(selectRefItems)
  const shipmentsData = useAppSelector(selectShipmentsData)

  const [customerHoldFlags, setCustomerHoldFlags] = useState<CustomerHoldFlags>({ showCreditHold: false, showDEAHold: false });
  const [upcomingShipmentsByMonth, setUpcomingShipmentsByMonth] = useState<Map<number, UpcomingShipment[]> | null>(null);

  const {
    autoShipments,
    upcomingShipments,
    medicationsMetadata,
    loadingState,
    shipmentCustomerNumber,
  } = shipmentsData

  useEffect(() => {
    const clearData = Boolean(shipmentCustomerNumber && customerNumber !== shipmentCustomerNumber)
    const fetchData = Boolean(customerNumber && (clearData || loadingState === LoadingState.NotStarted))

    if (fetchData) {
      dispatch(clearShipments())
      dispatch(clearRefItems())
      dispatch(getUpcomingShipmentData(customerNumber))
      dispatch(setShipmentCustomerNumber(customerNumber))
    }
  }, [customerNumber, shipmentCustomerNumber, loadingState])

  useEffect(() => {
    const needsRefItems = autoShipments?.length && !refItems

    if (needsRefItems) {
      dispatch(getRefItems({ customerNumber, autoShipments }))
    }
  }, [autoShipments, refItems])

  useEffect(() => {
    if (upcomingShipments && autoShipments) {
      const hasAutoShipmentData = autoShipments.length === 0
        || autoShipments.length && refItems

      if (upcomingShipments && hasAutoShipmentData) {
        const periodIds = getPeriodIds(upcomingShipments, autoShipments)
        const subscriptions = getSubscriptions(autoShipments, refItems)

        const entries = periodIds.map(periodId => {
          const upcoming = upcomingShipments[periodId] ?? []
          const subscription = subscriptions.get(periodId)

          const shipments = structuredClone(upcoming)

          if (subscription) {
            shipments.push(subscription as UpcomingShipment)
          }

          const entry: [number, UpcomingShipment[]] = [periodId, shipments]

          return entry
        })

        const shipmentsByMonth = new Map(entries)

        setUpcomingShipmentsByMonth(shipmentsByMonth)
      }
    }
  }, [upcomingShipments, autoShipments, refItems])

  const periodTotals: PeriodTotals | null = useMemo(() => {
    if (upcomingShipments) {
      let totals: PeriodTotals = {};

      for (let periodId in upcomingShipments) {
        if (!totals[periodId]) {
          totals[periodId] = { price: 0 };
        }

        for (let shipment of upcomingShipments[periodId]) {
          totals[periodId].price += parseFloat(shipment.price);
        }
      }

      return totals;
    } else {
      return null
    }
  }, [upcomingShipments]);

  useEffect(() => {
    const flags: CustomerHoldFlags = structuredClone(customerHoldFlags);

    if (upcomingShipments && Object.keys(upcomingShipments).length > 0) {
      const shipments = Object.values(upcomingShipments).flat()
      const hasSubstanceHold = shipments.some(shipment => shipment.hasControlledSubstance)

      flags.showCreditHold = true
      if (hasSubstanceHold) flags.showDEAHold = true;
    }

    setCustomerHoldFlags(flags);
  }, [upcomingShipments]);

  const upcomingShipmentKeys = useMemo(() => {
    if (upcomingShipmentsByMonth) {
      const keys = upcomingShipmentsByMonth.keys()
      const sorted = [...keys].sort((a: number, b: number) => (a - b))

      return sorted
    } else {
      return null
    }
  }, [upcomingShipmentsByMonth])

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Box>
          {medicationsMetadata && medicationsMetadata.backordered_meds > 0 && (
            <Box className={styles.alert} sx={{ mb: 2, p: 2 }}>
              <Typography style={{ fontSize: '16px' }}>
                You have <strong>{medicationsMetadata?.backordered_meds}</strong> items on backorder.{' '}
                <LinkButton
                  component="button"
                  onClick={() => {
                    navigate(isCorpView ? `/corp/practice/${customerNumber}/shipments/backorders` : '/practice/shipments/backorders');
                  }}
                >
                  See details
                </LinkButton>
              </Typography>
            </Box>
          )}
          {
            customerHoldFlags?.showCreditHold && (
              <CreditHoldBanner customer={customer} />
            )
          }
        </Box>

        {Boolean(upcomingShipmentKeys?.length) && upcomingShipmentKeys && upcomingShipmentsByMonth && (
          <Box>
            {upcomingShipmentKeys.map((key: number) => {
              const shipments = upcomingShipmentsByMonth.get(key)

              return Boolean(shipments?.length) && shipments && (
                <Card variant="outlined" className={styles.card} key={key}>
                  <div className={styles.boxHeader} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Typography variant="h4" style={{ fontSize: '17px' }}>
                      Shipments for {getDisplayPeriodName(shipments[0].refill_period_name)}
                    </Typography>
                    <Typography variant="h4" style={{ fontSize: '17px' }}>
                      {periodTotals && periodTotals[key] && periodTotals[key].price > 0 && (
                        <>
                          <span>Estimated Total *</span>
                          <span>${periodTotals[key].price}</span>
                        </>
                      )}
                    </Typography>
                  </div>
                  {shipments.map(shipment => (
                    <Box key={`${key}${shipment.refill_period_name}${shipment.type}${shipment.kit_id ?? 0}`} style={{ padding: '30px' }}>
                      {shipment.type === 'kit' && (
                        <Box style={{ padding: '30px' }}>
                          <Box style={{ display: 'flex' }} columnGap="16px">
                            <Box>
                              {shipment.kit_image ? (
                                <img className="kitImage" src={kitImages[`${shipment.kit_image}.png`]} alt="kit" style={{ width: '65px' }} />
                              ) : (
                                <img className="kitImage" src={kitImages[`default-kit-new.png`]} alt="kit" style={{ width: '65px' }} />
                              )}
                            </Box>
                            <Box style={{ alignContent: 'center' }}>
                              <Typography variant="h3" style={{ fontSize: '23px' }}>
                                {shipment.kit_model}
                                <LightTooltip
                                  style={{ display: 'inline-block', marginLeft: '10px' }}
                                  title={
                                    <React.Fragment>
                                      <Typography style={{ fontSize: '13px' }}>
                                        These items are on expiry based refill.{' '}
                                        <Link
                                          component="button"
                                          onClick={() => navigate(`${isCorpView ? `/corp/practice/${customerNumber}` : '/practice'}/auto-renewals`)}
                                        >
                                          Click here
                                        </Link>{' '}
                                        to view and manage your auto-replenishment settings.
                                      </Typography>
                                    </React.Fragment>
                                  }
                                  placement="bottom-start"
                                  arrow
                                >
                                  <IconButton sx={{ padding: '0px', width: '20px' }}>
                                    <Autorenew sx={{ color: shipment.refill_status === 1 || shipment.refill_status === 4 ? '#27c24c' : '#F4B122' }} />
                                  </IconButton>
                                </LightTooltip>
                                {shipment.refill_status === 2 ? (
                                  <Typography style={{ color: '#F4B122', fontSize: '13px', display: 'inline-block', marginLeft: '10px' }}>
                                    To Approve Your PO, Call {constants.HEALTHFIRST_PHONE_NUMBER}
                                  </Typography>
                                ) : null}
                              </Typography>
                              <Typography className="muted" style={{ fontSize: '13px' }}>
                                Kit #{shipment.kit_serial_number}
                              </Typography>
                            </Box>
                          </Box>
                          <Box className={styles.itemsList}>
                            {shipment.items.map(kitItem => (
                              <Box key={`${key}${shipment.refill_period_name}${shipment.type}${shipment.kit_id ?? 0}${kitItem.item_number}`} style={{ display: 'flex', marginTop: '15px', columnGap: '16px' }}>
                                <Box>
                                  {kitItem.image ? (
                                    <img className="med-image" src={itemImages[`${kitItem.image}.jpg`]} style={{ maxHeight: 50 }} />
                                  ) : (
                                    <img className="med-image" src={NoImage} style={{ maxHeight: 50 }} />
                                  )}
                                </Box>
                                <Box>
                                  <Typography variant="h4" style={{ fontSize: '17px' }}>
                                    {kitItem.quantity} {kitItem.name}
                                  </Typography>
                                  <Typography className="muted" style={{ fontSize: '13px' }}>
                                    Item # {kitItem.item_number}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {shipment.type !== 'kit' && (
                        <Box style={{ padding: '30px' }}>
                          <Box style={{ display: 'flex' }} columnGap="16px">
                            <Box>
                              {shipment.kit_image ? (
                                <img src={categoryImages[`${shipment.kit_image}.jpg`]} alt="kit" style={{ width: '65px' }} />
                              ) : (
                                <img className="med-image" src={kitImages['default-kit-new.png']} style={{ width: '65px' }} />
                              )}
                            </Box>
                            <Box style={{ alignContent: 'center' }}>
                              <Typography variant="h3" style={{ fontSize: '23px' }}>
                                {shipment.displayName}
                                <LightTooltip
                                  style={{ display: 'inline-block', marginLeft: '10px' }}
                                  title={
                                    <React.Fragment>
                                      <Typography style={{ fontSize: '13px' }}>
                                        These items are on expiry based refill.{' '}
                                        <Link
                                          component="button"
                                          onClick={() => navigate(`${isCorpView ? `/corp/practice/${customerNumber}` : '/practice'}/auto-renewals`)}>
                                          Click here
                                        </Link>{' '}
                                        to view and manage your auto-shipment settings.
                                      </Typography>
                                    </React.Fragment>
                                  }
                                  placement="bottom-start"
                                  arrow
                                >
                                  <IconButton sx={{ padding: '0px', width: '15px' }}>
                                    <Autorenew sx={{ color: '#27c24c' }} />
                                  </IconButton>
                                </LightTooltip>
                              </Typography>
                            </Box>
                          </Box>
                          <Box className={styles.itemsList}>
                            {shipment.items.map(kitItem => (
                              <Box key={`${key}${shipment.refill_period_name}${shipment.type}${shipment.kit_id ?? 0}${kitItem.item_number}`} style={{ display: 'flex', marginTop: '15px', columnGap: '16px' }}>
                                <Box>
                                  {kitItem.item_number ? (
                                    <img className="media-object" src={itemImages[`${kitItem.item_number}.jpg`]} style={{ maxHeight: 50 }} />
                                  ) : (
                                    <img className="med-image" src={NoImage} style={{ maxHeight: 50 }} />
                                  )}
                                </Box>
                                <Box>
                                  <Typography variant="h4" style={{ fontSize: '17px' }}>
                                    {kitItem.quantity} {kitItem.description}
                                  </Typography>
                                  <Typography className="muted" style={{ fontSize: '13px' }}>
                                    Item # {kitItem.item_number}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Card>
              );
            })}
          </Box>
        )}

        {upcomingShipmentsByMonth?.size === 0 && (
          <Box>
            <h4 style={{ color: '#5f5f5f' }}>There are currently no upcoming shipments expected.</h4>
          </Box>
        )}

        <Box className={styles.notice}>
          <Typography style={{ fontSize: '13px' }}>
            *Estimated Total as of <span>{(new Date()).toDateString()}</span>. Pricing subject to change.
          </Typography>
          <Typography style={{ fontSize: '13px' }}>
            Prices and items included in each shipment may vary due to market fluctuations and national drug shortages. Items on backorder will be
            shipped as soon as new inventory is received.
          </Typography>
          <Typography style={{ fontSize: '13px' }}>
            Estimated pricing includes DSCSA service fees, disposal costs, shipping and taxes (where applicable).
          </Typography>
          <Typography style={{ fontSize: '13px' }}>Items may ship separately.</Typography>
        </Box>
      </Box>
    </>
  );
};


export default UpcomingShipments;
